if (!customElements.get('asterion-addons')) {
  customElements.define('asterion-addons', class extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      this.dialog = this.querySelector('dialog');
      if (!this.form || !this.dialog || !this.dialog.showModal) return;
      this.owner = this.form.closest('product-form');
      this.submit = this.form.querySelector('[name="add"]');
      this.shop = this.form.querySelector('[data-asterion-shop]');
      this.nativeCheckout = this.form.querySelector('[data-asterion-native-checkout]');
      this.catalog = this.querySelector('[data-addon-scroll]');
      this.items = this.querySelector('[data-addon-items]');
      this.more = this.querySelector('[data-addon-more]');
      this.status = this.querySelector('[data-addon-status]');
      this.nextPage = this.querySelector('[data-addon-page]')?.dataset.nextPage || '';
      this.more.hidden = !this.nextPage;
      if ('IntersectionObserver' in window) {
        this.pageObserver = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting) && this.autoLoad) this.loadMore();
        }, { root: this.catalog, rootMargin: '200px 0px' });
      }
      this.captureSubmit = this.onSubmit.bind(this);
      document.addEventListener('submit', this.captureSubmit, true);
      this.onShop = () => this.open('shop');
      this.shop?.addEventListener('click', this.onShop);
      if (this.shop && this.nativeCheckout) {
        this.shop.hidden = false;
        this.nativeCheckout.hidden = true;
        this.observer = new MutationObserver(() => {
          this.shop.disabled = this.submit.disabled || this.submit.getAttribute('aria-disabled') === 'true';
        });
        this.observer.observe(this.submit, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
        this.shop.disabled = this.submit.disabled;
      }
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.finish(false);
      });
      this.dialog.addEventListener('click', event => {
        if (event.target === this.dialog || event.target.closest('[data-addon-close], [data-addon-skip]')) this.finish(false);
        if (event.target.closest('[data-addon-continue]')) this.finish(true);
        if (event.target.closest('[data-addon-more]')) {
          this.autoLoad = true;
          this.loadMore();
        }
        const button = event.target.closest('[data-addon-select]');
        if (button) this.select(button.closest('[data-addon-card]'));
      });
      this.dialog.addEventListener('close', () => {
        if (this.pending) this.finish(false);
      });
      this.dialog.addEventListener('change', event => {
        const select = event.target.closest('[data-addon-variant]');
        if (!select) return;
        const card = select.closest('[data-addon-card]');
        const option = select.selectedOptions[0];
        card.querySelector('[data-addon-price]').textContent = option.dataset.price;
        card.querySelector('[data-addon-original]').textContent = option.dataset.original;
      });
    }

    disconnectedCallback() {
      document.removeEventListener('submit', this.captureSubmit, true);
      this.shop?.removeEventListener('click', this.onShop);
      this.observer?.disconnect();
      this.stopLoading();
      this.pending = null;
      if (this.dialog?.open) this.dialog.close();
      document.body.classList.remove('asterion-addons-open');
    }

    onSubmit(event) {
      if (event.target !== this.form) return;
      if (this.resume) {
        this.resume = false;
        return;
      }
      if (this.submit.disabled || this.submit.getAttribute('aria-disabled') === 'true') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.open('cart');
    }

    open(action) {
      if (this.pending || this.submit.disabled || this.submit.getAttribute('aria-disabled') === 'true') return;
      if (!this.form.reportValidity()) return;
      const data = new FormData(this.form);
      if (!/^\d+$/.test(data.get('id') || '')) return;
      this.pending = { action, data };
      this.selected = null;
      this.querySelector('.asterion-addons__error').hidden = true;
      this.querySelectorAll('[data-addon-card]').forEach(card => this.updateCard(card, false));
      this.querySelector('[data-addon-continue]').hidden = true;
      document.body.classList.add('asterion-addons-open');
      this.dialog.showModal();
      this.autoLoad = true;
      if (this.nextPage) this.pageObserver?.observe(this.more);
    }

    async loadMore() {
      if (!this.pending || !this.nextPage || this.loadController) return;
      const controller = new AbortController();
      this.loadController = controller;
      this.more.disabled = true;
      this.catalog.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.loadingLabel;
      this.status.hidden = false;
      try {
        const url = new URL(this.nextPage, window.location.href);
        if (url.origin !== window.location.origin) throw new Error('Invalid catalog URL');
        url.searchParams.set('section_id', 'asterion-addon-products');
        const response = await fetch(url.href, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Catalog unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const page = html.querySelector('[data-addon-page]');
        if (!page) throw new Error('Missing catalog page');
        if (this.loadController !== controller || !this.pending) return;
        const nextPage = page.dataset.nextPage || '';
        if (nextPage === this.nextPage) throw new Error('Repeated catalog page');
        const seen = new Set([...this.items.querySelectorAll('[data-addon-card]')].map(card => card.dataset.productId));
        for (const card of page.querySelectorAll('[data-addon-card]')) {
          if (seen.has(card.dataset.productId)) continue;
          seen.add(card.dataset.productId);
          this.items.append(card);
        }
        this.nextPage = nextPage;
        this.more.hidden = !this.nextPage;
        if (!this.nextPage) this.pageObserver?.disconnect();
        this.status.hidden = true;
      } catch (error) {
        if (this.loadController !== controller || error.name === 'AbortError') return;
        this.autoLoad = false;
        this.status.textContent = this.dataset.loadError;
        this.status.hidden = false;
      } finally {
        if (this.loadController === controller) {
          this.loadController = null;
          this.more.disabled = false;
          this.catalog.removeAttribute('aria-busy');
        }
      }
    }

    stopLoading() {
      this.pageObserver?.disconnect();
      this.loadController?.abort();
      this.loadController = null;
      if (this.more) this.more.disabled = false;
      if (this.status) this.status.hidden = true;
      this.catalog?.removeAttribute('aria-busy');
    }

    updateCard(card, selected) {
      card.classList.toggle('is-selected', selected);
      const button = card.querySelector('[data-addon-select]');
      button.setAttribute('aria-pressed', String(selected));
      button.setAttribute('aria-expanded', String(selected));
      card.querySelector('[data-addon-options]').hidden = !selected;
    }

    select(card) {
      this.selected = this.selected === card ? null : card;
      this.querySelectorAll('[data-addon-card]').forEach(item => this.updateCard(item, item === this.selected));
      this.querySelector('[data-addon-continue]').hidden = !this.selected;
    }

    finish(includeAddon) {
      if (!this.pending) return;
      const addon = includeAddon ? this.selected?.querySelector('[data-addon-variant]')?.value : null;
      if (includeAddon && !/^\d+$/.test(addon || '')) {
        this.querySelector('.asterion-addons__error').hidden = false;
        return;
      }
      const pending = this.pending;
      this.pending = null;
      this.stopLoading();
      document.body.classList.remove('asterion-addons-open');
      this.dialog.close();
      if (pending.action === 'shop') {
        const variant = pending.data.get('id');
        const quantity = Math.max(1, parseInt(pending.data.get('quantity'), 10) || 1);
        const items = [variant + ':' + quantity];
        if (addon) items.push(addon + ':1');
        const url = new URL(this.dataset.root + 'cart/' + items.join(','), window.location.origin);
        url.searchParams.set('payment', 'shop_pay');
        const properties = {};
        pending.data.forEach((value, key) => {
          const match = key.match(/^properties\[([^\]]+)\]$/);
          if (match && typeof value === 'string') properties[match[1]] = value;
        });
        if (Object.keys(properties).length) {
          const bytes = new TextEncoder().encode(JSON.stringify(properties));
          url.searchParams.set('properties', btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''));
        }
        window.location.assign(url.href);
        return;
      }
      if (addon) this.form.asterionAddon = addon;
      this.resume = true;
      this.form.requestSubmit(this.submit);
      this.resume = false;
    }
  });
}
